import React, { useContext, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { OperationType } from 'relay-runtime';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { DecomposedFieldEntryPointContext } from '../decomposed-field-entry-point-provider/index.tsx';

export type DefaultRuntimeProps = {
	isCardFocused?: boolean;
	isCardActive?: boolean;
};

type EntryPointParams = {
	cloudId: string;
	issueKey: string;
};

type DecomposedAsyncFieldWrapperProps<
	EntryPointQueryType extends Record<string, OperationType>,
	FieldRuntimeProps,
> = {
	staticView: ReactNode;
	issueKey: string;
	entryPoint: EntryPoint<EntryPointComponent<EntryPointQueryType, {}, {}, {}>, EntryPointParams>;
	fieldName: string;
	runtimeProps?: DefaultRuntimeProps & FieldRuntimeProps;
};

const EMPTY_OBJECT = {};

export const DecomposedAsyncFieldWrapper = <
	EntryPointQueryType extends Record<string, OperationType>,
	FieldRuntimeProps extends Record<string, unknown> = {},
>({
	staticView,
	issueKey,
	entryPoint,
	fieldName,
	runtimeProps,
}: DecomposedAsyncFieldWrapperProps<EntryPointQueryType, FieldRuntimeProps>) => {
	const cloudId = useCloudId();
	const entryPointParams = useMemo(
		() => ({
			cloudId,
			issueKey,
		}),
		[cloudId, issueKey],
	);
	const runtimePropsParams = useMemo(
		() => runtimeProps || (fg('jfp-magma-concurrent-board-perf-2') ? EMPTY_OBJECT : {}),
		[runtimeProps],
	);

	// useEntryPoint is used here as Platform Entrypoints API does not support loading entrypoints on hover.
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entryPoint,
		entryPointParams,
	);
	const { registerEntryPointLoad, getIsEntryPointsLoaded } = useContext(
		DecomposedFieldEntryPointContext,
	);

	// Entrypoint is not being loaded here - moving function into context
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		const deregister = registerEntryPointLoad({
			fieldName,
			onEntryPointLoad: entryPointActions.load,
		});
		return deregister;
	}, [entryPointActions.load, fieldName, registerEntryPointLoad]);

	const renderErrorFallback = useCallback(() => staticView, [staticView]);

	if (!getIsEntryPointsLoaded()) {
		return staticView;
	}

	return (
		<JiraEntryPointContainer
			id="decomposed-async-field-wrapper"
			entryPointReferenceSubject={entryPointReferenceSubject}
			runtimeProps={runtimePropsParams}
			fallback={staticView}
			errorFallback={renderErrorFallback}
		/>
	);
};
